import React, { memo, useEffect, useRef, useState } from "react";
import {
  getPdfDocumentAction,
  PdfDocumentAction,
  setPdfDocumentAction,
  subscribePdfDocumentAction,
} from "app/data/shipping-docs/pdfDocumentActionStorage";
import Button, { ButtonSizes, ButtonThemes } from "ui-components/button/button";
import XGSIcon from "ui-components/icon/xgsIcon";
import XGSIcons from "ui-components/icon/xgsIcons";
import Loading from "ui-components/loading/loading";

import "./pdfActionSplitButton.scss";

export interface PdfActionSplitButtonProps {
  onAction: (action: PdfDocumentAction) => void;
  disabled?: boolean;
  spinner?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const ACTION_LABEL: Record<PdfDocumentAction, string> = {
  print: "Print",
  download: "Download",
};

const ACTION_ICON = {
  print: XGSIcons.faPrint,
  download: XGSIcons.faDownload,
};

const PdfActionSplitButton: React.FC<PdfActionSplitButtonProps> = memo((props) => {
  const { onAction, disabled, spinner, className, children } = props;
  const [action, setAction] = useState<PdfDocumentAction>(getPdfDocumentAction);
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => subscribePdfDocumentAction(setAction), []);

  useEffect(() => {
    if (!menuOpen) return;

    const handleDocumentClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [menuOpen]);

  const isDisabled = !!disabled || !!spinner;

  const handlePrimaryClick = () => {
    if (isDisabled) return;
    setMenuOpen(false);
    onAction(action);
  };

  const handleSelect = (selected: PdfDocumentAction) => {
    setPdfDocumentAction(selected);
    setAction(selected);
    setMenuOpen(false);
  };

  const rootClass = ["xgs-pdf-action-split", className].filter(Boolean).join(" ");

  return (
    <div className={rootClass} ref={rootRef}>
      <Button
        type="button"
        theme={ButtonThemes.blue}
        size={ButtonSizes.autoMedium}
        className="xgs-pdf-action-split__primary"
        onClick={handlePrimaryClick}
        disabled={isDisabled}
        spinner={false}
      >
        <span className="xgs-pdf-action-split__primary-content">
          {spinner ? (
            <Loading isLoading={true} />
          ) : (
            <XGSIcon icon={ACTION_ICON[action]} />
          )}
          <span>
            {ACTION_LABEL[action]}
            {children != null && children !== "" ? <> {children}</> : null}
          </span>
        </span>
      </Button>
      <button
        type="button"
        className="xgs-pdf-action-split__chevron"
        disabled={isDisabled}
        aria-label="Choose print or download"
        aria-expanded={menuOpen}
        onClick={() => {
          if (isDisabled) return;
          setMenuOpen((open) => !open);
        }}
      >
        <XGSIcon icon={XGSIcons.faChevronDown} />
      </button>
      {menuOpen && (
        <div className="xgs-pdf-action-split__menu" role="menu">
          {(["print", "download"] as PdfDocumentAction[]).map((option) => (
            <button
              key={option}
              type="button"
              role="menuitem"
              className={`xgs-pdf-action-split__menu-item${
                option === action ? " xgs-pdf-action-split__menu-item--active" : ""
              }`}
              onClick={() => handleSelect(option)}
            >
              <span className="xgs-pdf-action-split__menu-check">
                {option === action ? <XGSIcon icon={XGSIcons.faCheck} /> : null}
              </span>
              <XGSIcon icon={ACTION_ICON[option]} />
              <span>{ACTION_LABEL[option]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

export default PdfActionSplitButton;
